import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { expenditureCodesAndNames, formatToZMW, revenueCodesAndNames, rootUrl } from '../../../helpers';
import { useAuth } from '../../../context/auth-context';
import { useOutletContext } from 'react-router-dom';
import { FaFolderOpen, FaSpinner, FaTable, FaFilter, FaSearch, FaBuilding, FaCode, FaFileInvoice, FaUniversity, FaMapMarkerAlt, FaUser, FaProjectDiagram } from 'react-icons/fa';
import Select from 'react-select';
import { toast } from 'react-toastify';

export default function Pivot({ startDate, endDate, triggerFetch }) {
  const [cashData, setCashData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [awaitingFilters, setAwaitingFilters] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 1000;

  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedCOA, setSelectedCOA] = useState(null);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedCollectionCenter, setSelectedCollectionCenter] = useState(null);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [descriptionSearch, setDescriptionSearch] = useState('');

  const auth = useAuth();
  const { allData } = useOutletContext();

  var codeNames = [
    { id: "151", name: "LOCAL TAXES" },
    { id: "152", name: "FEES AND CHARGES" },
    { id: "153", name: "LICENCES" },
    { id: "154", name: "LEVIES" },
    { id: "155", name: "PERMITS" },
    { id: "156", name: "CHARGES" },
    { id: "157", name: "OTHER INCOMES" },
    { id: "158", name: "NATIONAL SUPPORT (GRANTS)" },
    { id: "159", name: "DONOR SUPPORT" },
    { id: "111", name: "Total" },
    { id: "211", name: "Salaries" },
    { id: "212", name: "Wages" },
    { id: "213", name: "Allowances" },
    { id: "214", name: "Emolument Expenses" },
    { id: "221", name: "Office Costs" },
    { id: "222", name: "Building, Repair and Maintenance Costs" },
    { id: "223", name: "Plant, Machinery, Vehicle Running and Maintenance Costs" },
    { id: "224", name: "Other Administrative Operating Costs" },
    { id: "225", name: "Requisites" },
    { id: "226", name: "Services" },
    { id: "227", name: "Travel Expenses" },
    { id: "228", name: "Training" },
    { id: "229", name: "Legal Costs" },
    { id: "231", name: "Depreciation on Own Assets" },
    { id: "232", name: "Depreciation on Leased Property (Finance Lease)" },
    { id: "241", name: "Interest on Domestic Debt" },
    { id: "242", name: "Interest on External Debt" },
    { id: "243", name: "Financial Charges" },
    { id: "244", name: "Settlement of Outstanding Bills - Financial Charges" },
    { id: "249", name: "Other Financial Charges" },
    { id: "251", name: "Social Assistance Benefits" },
    { id: "261", name: "Grants to Grant-aided Institutions" },
    { id: "262", name: "Grants to Non-Governmental Organisations" },
    { id: "263", name: "Grants to Households" },
    { id: "264", name: "Grants to Institutional Revolving Funds" },
    { id: "265", name: "Other Grants" },
    { id: "267", name: "Other Payments" },
    { id: "271", name: "Subsidies to Non-Financial Enterprises" },
    { id: "272", name: "Subsidies to Financial Institutions" },
    { id: "273", name: "Subsidies to Private Enterprises" },
    { id: "274", name: "Other Subsidies" },
    { id: "281", name: "Legal Expenses" },
    { id: "291", name: "Constitutional Expenditure" },
    { id: "292", name: "Other Constitutional Expenditure" },
    { id: "293", name: "Allowances" },
    { id: "294", name: "Statutory Expenditure" },
    { id: "295", name: "Other Expenses" },
    { id: "311", name: "Fixed Assets" },
    { id: "312", name: "Stocks (Inventories)" },
    { id: "313", name: "Valuables" },
    { id: "314", name: "Non-Produced Assets" },
    { id: "320", name: "Long Term Investments" },
    { id: "321", name: "Debtors" },
    { id: "322", name: "Staff Debtors" },
    { id: "323", name: "Prepayments" },
    { id: "324", name: "VAT refunds" },
    { id: "325", name: "Foreign Debtors" },
    { id: "326", name: "Cash and Cash Equivalents" },
    { id: "411", name: "Creditors" },
    { id: "421", name: "Creditors" },
    { id: "520", name: "Current Year Surplus/(Deficit)" },
    { id: "530", name: "Reserves" },
    { id: "611", name: "Realised Gains/Losses" },
    { id: "621", name: "Unrealised Gains/Losses" },
    { id: "631", name: "Miscellaneous" },
    { id: "510", name: "Owners Fund" },
    { id: "000", name: "Rest" },]
  const codeLabelMap = [...revenueCodesAndNames, ...expenditureCodesAndNames].reduce((acc, item) => {
    acc[String(item.id)] = item.name;
    return acc;
  }, {});
  const fetchPivotData = async ({ page = 1, append = false } = {}) => {
    if (!startDate || !endDate || !auth?.user?.id) return;
    setLoading(true);
    try {
      const response = await axios.post(rootUrl('/cashReportQuery'), {
        uid: auth.user.id,
        startDate,
        endDate,
        codePrefix: selectedCode?.value || '',
        type: selectedType?.value || '',
        coaCode: selectedCOA?.value || '',
        bank: selectedBanks.length ? selectedBanks.map((bank) => bank.value).join('|') : '',
        program: selectedProgram?.value || '',
        collectionCenter: selectedCollectionCenter?.value || '',
        payee: selectedPayee?.value || '',
        project: selectedProject?.value || '',
        search: descriptionSearch || '',
        page,
        limit: PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      const rows = payload.transactions || [];
      setCashData(append ? [...cashData, ...rows] : rows);
      setHasMore(Boolean(payload?.pagination?.hasMore));
      setCurrentPage(page);
      setAwaitingFilters(false);
    } catch (error) {
      console.error('Error fetching pivot data', error);
      if (!append) {
        setCashData([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      setCashData([]);
      setHasMore(false);
      setCurrentPage(1);
      setAwaitingFilters(true);
    }
  }, [triggerFetch, startDate, endDate]);

  const uniqueCodes = [...new Set(allData?.budget?.map(b => b.coa.toString().substring(0, 3)))];

  // Sort loaded data by code (first 3 digits of COA) and then by COA
  const sortedData = [...cashData].sort((a, b) => {
    const coaA = a.coa_code || allData?.budget?.find((x) => x.id === a.coa_id)?.coa || '';
    const coaB = b.coa_code || allData?.budget?.find((x) => x.id === b.coa_id)?.coa || '';
    const codeA = coaA.toString().substring(0, 3);
    const codeB = coaB.toString().substring(0, 3);
    if (codeA !== codeB) return codeA.localeCompare(codeB, undefined, { numeric: true });
    return coaA.toString().localeCompare(coaB.toString(), undefined, { numeric: true });
  });


  // Group by code and COA for subtotals
  const groupedRows = [];
  let currentCode = null;
  let currentCOA = null;
  let coaRows = [];
  let codeRows = [];
  let coaDebit = 0, coaCredit = 0;
  let codeDebit = 0, codeCredit = 0;

  const pushCOASubtotal = (coa, code) => {
    if (coaRows.length > 0) {
      groupedRows.push(
        <tr key={`subtotal-coa-${coa}`} className="bg-gray-100 font-semibold border-t-2 border-gray-300">
          <td colSpan={15} className="px-4 py-3 text-gray-800">Subtotal for COA {coa}</td>
          <td className="px-4 py-3 text-right text-gray-800 font-medium">{formatToZMW(coaDebit)}</td>
          <td className="px-4 py-3 text-right text-gray-800 font-medium">{formatToZMW(coaCredit)}</td>
        </tr>
      );
      coaRows = [];
      coaDebit = 0;
      coaCredit = 0;
    }
  };
  const pushCodeSubtotal = (code) => {
    if (codeRows.length > 0) {
      groupedRows.push(
        <tr key={`subtotal-code-${code}`} className="bg-blue-100 font-bold border-t-2 border-blue-300">
          <td colSpan={15} className="px-4 py-3 text-blue-900">Subtotal for Code {code}</td>
          <td className="px-4 py-3 text-right text-blue-900">{formatToZMW(codeDebit)}</td>
          <td className="px-4 py-3 text-right text-blue-900">{formatToZMW(codeCredit)}</td>
        </tr>
      );
      codeRows = [];
      codeDebit = 0;
      codeCredit = 0;
    }
  };

  sortedData.forEach((item, index) => {
    const coaObj = allData?.budget?.find((x) => x.id === item.coa_id);
    const coa = item.coa_code || coaObj?.coa || item.coa || '';
    if (!coa) return; // Skip rows with undefined or blank COA
    const code = coa.toString().substring(0, 3);
    const program = item.program_code || coaObj?.program || item.program;
    const subprogram = item.subprogram_code || coaObj?.subprogram || item.subprogram;
    const debit = item.type === 'income' ? Number(item.amount) : 0;
    const credit = item.type === 'expense' ? Number(item.amount) : 0;

    // If new code group, push previous code subtotal
    if (currentCode !== null && code !== currentCode) {
      pushCOASubtotal(currentCOA, currentCode);
      pushCodeSubtotal(currentCode);
      currentCOA = null;
    }
    // If new COA group, push previous COA subtotal
    if (currentCOA !== null && coa !== currentCOA) {
      pushCOASubtotal(currentCOA, currentCode);
    }

    groupedRows.push(
      <tr key={index} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
        <td className="px-4 py-2.5 text-sm text-gray-700 font-medium">{code}</td>
        <td className="px-4 py-2.5 text-sm text-gray-800">{codeNames.find(a=>a.id == code)?.name || item.codeName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700 font-medium">{coa}</td>
        <td className="px-4 py-2.5 text-sm text-gray-800">{allData.coa.find(a=>a.account == coa)?.name || item.coaName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData.program?.find(p => p.programCode === program)?.name || item.programName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData.subProgram?.find(s => s.subProgramCode == subprogram && s.programCode == program)?.name || item.subProgramName || ''}</td>
        <td title={item.description} className="px-4 py-2.5 text-sm text-gray-600 max-w-xs truncate">{item.description}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData?.suppliers?.find((s) => s.id == item.paye)?.name || item.payeeName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData?.cc?.find((s) => s.id == item.cc_id)?.collection_name || 'Civic Center' || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{item.cheque}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{item.date?.substring(0, 10)}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData.sof?.find(sof => sof.id === item.sof)?.name || item.sofName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{item.voucher}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData.bank?.find(b => b.id === item.bank)?.name || item.bankName || ''}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{allData?.projects?.find(p => p.id == item.proj_id)?.name || item.project_name || ''}</td>
        <td className="px-4 py-2.5 text-sm text-right text-green-700 font-medium">{debit ? formatToZMW(debit) : '-'}</td>
        <td className="px-4 py-2.5 text-sm text-right text-red-700 font-medium">{credit ? formatToZMW(credit) : '-'}</td>
      </tr>
    );
    coaRows.push(item);
    codeRows.push(item);
    coaDebit += debit;
    coaCredit += credit;
    codeDebit += debit;
    codeCredit += credit;
    currentCOA = coa;
    currentCode = code;
  });
  // Push last group subtotals
  pushCOASubtotal(currentCOA, currentCode);
  pushCodeSubtotal(currentCode);

  const hasAtLeastOneFilter = Boolean(
    selectedCode?.value ||
    selectedType?.value ||
    selectedCOA?.value ||
    selectedBanks.length > 0 ||
    selectedProgram?.value ||
    selectedCollectionCenter?.value ||
    selectedPayee?.value ||
    selectedProject?.value ||
    descriptionSearch?.trim()
  );

  const handleSearch = () => {
    if (!hasAtLeastOneFilter) {
      toast.info('Select at least one filter before searching.');
      return;
    }
    fetchPivotData({ page: 1, append: false });
  };

  const handleLoadMore = () => {
    if (!hasMore || loading) return;
    fetchPivotData({ page: currentPage + 1, append: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-950 rounded-lg">
          <FaTable className="text-white text-xl" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Pivot Report</h1>
          <p className="text-sm text-gray-500">Detailed transaction analysis with grouping</p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <FaCode className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Code"
              options={uniqueCodes.map(code => ({ value: code, label: `${code} - ${codeLabelMap[String(code)] || 'Unknown'}` }))}
              value={selectedCode}
              onChange={setSelectedCode}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaCode className="text-gray-500 text-sm" />
            <Select
              placeholder="Type"
              options={[
                { value: 'income', label: 'Income' },
                { value: 'expense', label: 'Expense' },
              ]}
              value={selectedType}
              onChange={setSelectedType}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaFileInvoice className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by COA"
              options={allData?.budget?.map(b => ({ value: b.coa, label: b.coa }))}
              value={selectedCOA}
              onChange={setSelectedCOA}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaBuilding className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Bank"
              options={allData?.bank?.map(b => ({ value: b.id, label: b.name }))}
              value={selectedBanks}
              onChange={setSelectedBanks}
              isMulti
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaUniversity className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Program"
              options={allData?.program?.map(p => ({ value: p.programCode, label: p.name }))}
              value={selectedProgram}
              onChange={setSelectedProgram}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaMapMarkerAlt className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Collection Center"
              options={allData?.cc?.map(cc => ({ value: cc.id, label: cc.collection_name }))}
              value={selectedCollectionCenter}
              onChange={setSelectedCollectionCenter}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaUser className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Payee"
              options={allData?.suppliers?.map(s => ({ value: s.id, label: s.name }))}
              value={selectedPayee}
              onChange={setSelectedPayee}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <FaProjectDiagram className="text-gray-500 text-sm" />
            <Select
              placeholder="Filter by Project"
              options={allData?.projects?.map(p => ({ value: p.id, label: p.name ? `${p.name}${p.description ? ` - ${p.description}` : ''}` : (p.description || p.code || `Project ${p.id}`) }))}
              value={selectedProject}
              onChange={setSelectedProject}
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 md:col-span-2 lg:col-span-1">
            <FaSearch className="text-gray-500 text-sm" />
            <input
              type="text"
              placeholder="Search Description"
              value={descriptionSearch}
              onChange={e => setDescriptionSearch(e.target.value)}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-5 py-2.5 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm flex items-center justify-center gap-2 shadow-sm"
          >
            <FaFilter className="text-xs" />
            Search
          </button>
          <button
            onClick={() => {
              setSelectedCode(null);
              setSelectedType(null);
              setSelectedCOA(null);
              setSelectedBanks([]);
              setSelectedProgram(null);
              setSelectedCollectionCenter(null);
              setSelectedPayee(null);
              setSelectedProject(null);
              setDescriptionSearch('');
            }}
            className="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm flex items-center justify-center gap-2 shadow-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className='flex flex-col justify-center items-center h-64 bg-gray-50 rounded-xl border border-gray-200'>
          <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
          <p className="text-gray-600">Loading pivot data...</p>
        </div>
      ) : (
        <>
          {awaitingFilters && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              Date range is set. Add one or more filters, then click <strong>Search</strong> to fetch chunked data from backend.
            </div>
          )}
          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                    <th className="px-4 py-3 text-left font-semibold text-sm">Code</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Code Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">COA</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">COA Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Program</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Subprogram</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Description</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Payee</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Collection Center</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Cheque</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Source Of Fund</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">PV</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Bank</th>
                    <th className="px-4 py-3 text-left font-semibold text-sm">Project</th>
                    <th className="px-4 py-3 text-right font-semibold text-sm">Debit</th>
                    <th className="px-4 py-3 text-right font-semibold text-sm">Credit</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {groupedRows.length > 0 ? groupedRows : (
                    <tr>
                      <td colSpan={17} className="text-center py-12">
                        <div className="flex flex-col justify-center items-center">
                          <div className="p-4 bg-gray-100 rounded-full mb-4">
                            <FaFolderOpen className="text-gray-400 text-5xl" />
                          </div>
                          <p className="text-gray-600 text-lg font-medium">No data available</p>
                          <p className="text-gray-500 text-sm mt-1">Try adjusting your filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {/* Final total row */}
                  {groupedRows.length > 0 && (
                    <tr className="bg-gradient-to-r from-green-600 to-green-700 text-white border-t-4 border-green-800">
                      <td colSpan={15} className="px-4 py-4 font-bold text-base">Grand Total</td>
                      <td className="px-4 py-4 text-right font-bold text-base">{formatToZMW(sortedData.reduce((sum, item) => sum + (item.type === 'income' ? Number(item.amount) : 0), 0))}</td>
                      <td className="px-4 py-4 text-right font-bold text-base">{formatToZMW(sortedData.reduce((sum, item) => sum + (item.type === 'expense' ? Number(item.amount) : 0), 0))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {hasMore && !awaitingFilters && (
            <div className="flex justify-center">
              <button
                onClick={handleLoadMore}
                className="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm"
              >
                Load Next 1,000 Rows
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
